import { useTranslation } from 'next-i18next'
import braintreeApi, { BraintreeToken } from 'redux/services/braintree'
import braintree, { PayPal, PayPalTokenizePayload } from 'braintree-web'
import mParticleSdk from '@mparticle/web-sdk'
import { PaymentSelectionButtonProps } from '../payment-selection-button'
import { ActionButton } from '../../../../../ui/atoms/ActionButton'
import React, { useEffect, useState } from 'react'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { setIsAmex, setPaymentMethod } from '../../../../../order/state/payment-details'
import Placeloader from '../../../../../ui/atoms/Loaders/Placeloader'
import Bugsnag from '@bugsnag/js'
import useTrackUIElementEvent from '../../../../../analytics/hooks/useTrackUIElementEvent'
import useStatus from '../../../../../status/state/use-status'
import useOrder from '../../../../../order/state/order/use-order'
import useConfig from '../../../../../config/state/use-config'
import { StatusType } from '../../../../../status/state/status-types'
import { trackFormSubmitEvent } from '../../../../../analytics/helpers/track-form-events'
import useFlow from '../../../../state/use-flow'
import { Logo } from '../../../../../ui/atoms/Logo'

export const PaypalBraintree = ({ dataId, ariaLabel, onClick }: PaymentSelectionButtonProps) => {
  const { t } = useTranslation()

  const config = useConfig()

  const order = useOrder()

  const campaignId = config.campaignId

  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const flow = useFlow()

  const goNext = () => {
    flow.nextStep && flow.goToStep(flow.nextStep.name)
  }

  const [isLoading, setIsLoading] = useState(true)
  const [paypalInstance, setPaypalInstance] = useState<PayPal | null>(null)
  const status = useStatus()

  const { data: braintreeToken } = braintreeApi.useGetBraintreeTokenQuery(
    {
      campaignId,
      currency: order.currency.value ?? 'USD',
    } as { campaignId: string; currency: string },
    { skip: !campaignId },
  ) as { data?: BraintreeToken }

  const trackEvent = useTrackUIElementEvent()

  const handleOnClick = () => {
    if (onClick) {
      onClick()
    }

    trackFormSubmitEvent(flow.currentStep.name)

    if (!paypalInstance) return

    paypalInstance.tokenize(
      {
        flow: 'vault',
        // For more tokenization options, see the full PayPal tokenization documentation
        // https://braintree.github.io/braintree-web/current/PayPal.html#tokenize
      },
      function (tokenizeErr, payload?: PayPalTokenizePayload) {
        if (tokenizeErr) {
          if (tokenizeErr.type !== 'CUSTOMER') {
            console.error('Error tokenizing:', tokenizeErr)
            Bugsnag.notify(`Error tokenizing: ${tokenizeErr.message} in PaypalBraintree/index.tsx`)
          } else {
            trackEvent('Modal Cancelled', mParticleSdk.EventType.Other, {
              tracking_id: 'payment details: ' + 'PayPal_Braintree',
            })
          }

          return
        }

        if (!payload) {
          return
        }

        if (payload.nonce) {
          // Tokenization succeeded
          dispatch(setPaymentMethod({ nonce: payload.nonce }))

          // Update billing details
          const address = payload.details?.billingAddress

          order.contact.setValue({
            ...order.contact.value,
            email: payload.details?.email ?? '',
            firstName: payload.details?.firstName ?? '',
            lastName: payload.details?.lastName ?? '',
            phone: payload.details?.phone ?? '',
          })

          order.address.setValue({
            city: address?.city ?? '',
            country: address?.countryCode ?? '',
            line1: address?.line1 ?? '',
            line2: address?.line2 ?? '',
            zipCode: address?.postalCode ?? '',
            state: address?.state ?? '',
          })

          // paypal doesn't provide the card type,
          // but the donor may have entered an amex card then switched payment method
          dispatch(setIsAmex(false))

          goNext()
        }
      },
    )
  }

  useEffect(() => {
    if (!braintreeToken) return

    braintree.client
      .create({
        authorization: braintreeToken.token.data,
      })
      .then(function (clientInstance) {
        return braintree.paypal.create({
          client: clientInstance,
        })
      })
      .then(function (paypalInstance) {
        setPaypalInstance(paypalInstance)
        setIsLoading(false)
      })
      .catch(function (err) {
        console.error('Error!', err)
        Bugsnag.notify(`Braintree client error in PaypalBraintree/index.tsx: ${err}`)
        setIsLoading(false)
      })
  }, [braintreeToken])

  return (
    <>
      {isLoading && (
        <Placeloader
          data-testid="paypal_braintree_place_loader"
          style={{ width: '100%', height: 57, borderRadius: 48 }}
        />
      )}

      {!isLoading && (
        <ActionButton
          dataId={dataId}
          onClick={handleOnClick}
          color={DEFAULT_THEME_COLORS['--theme-color--brand-paypal']}
          disabled={status.type === StatusType.PROCESSING}
          ariaLabel={ariaLabel}
        >
          <Logo name="paypal" altText={t('PaypalBraintree.paypalLogo.altText', 'PayPal')} />
        </ActionButton>
      )}
    </>
  )
}
